import React, { useState, useEffect } from 'react';
import { X, Smartphone, Monitor, Globe, Tablet, Trash2, Loader2, Copy } from 'lucide-react';
import { toast } from 'react-toastify';
import { LysepAPI } from '../../../contexts/ApiContext';

const UserDevicesModal = ({ isOpen, onClose, user }) => {
    const [devices, setDevices] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isOpen && user?.id) {
            fetchUserDevices(user.id);
        } else {
            setDevices([]);
        }
    }, [isOpen, user]);

    const fetchUserDevices = async (userId) => {
        try {
            setLoading(true);
            const response = await LysepAPI.get(`/api/user/${userId}/device-tokens`);

            if (response.data?.state && response.data?.data) {
                setDevices(response.data.data.tokens || response.data.data || []);
            } else {
                toast.error('Cihaz listesi alınamadı');
                setDevices([]);
            }
        } catch (error) {
            console.error('Fetch devices error:', error);
            const errorMessage = error.response?.data?.message || 'Cihaz listesi alınamadı';
            toast.error(errorMessage);
            setDevices([]);
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    const getPlatformIcon = (platform) => {
        switch (platform?.toLowerCase()) {
            case 'android':
                return <Smartphone className="w-4 h-4 text-green-500" />;
            case 'ios':
                return <Tablet className="w-4 h-4 text-blue-500" />; // Or Smartphone
            case 'web':
                return <Globe className="w-4 h-4 text-cyan-500" />;
            case 'desktop':
                return <Monitor className="w-4 h-4 text-amber-500" />;
            default:
                return <Monitor className="w-4 h-4 text-gray-500" />;
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            return new Date(dateString).toLocaleString('tr-TR');
        } catch {
            return dateString;
        }
    };

    const copyToClipboard = (text) => {
        if (!text) return;
        navigator.clipboard.writeText(text);
        toast.success('Kopyalandı', { autoClose: 1000, hideProgressBar: true });
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-fadeIn">
            <div className="bg-[#1a1a1a] border border-white/10 rounded-xl w-full max-w-5xl h-[80vh] flex flex-col shadow-2xl">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-white/10 bg-[#252525]">
                    <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                        <Smartphone className="w-5 h-5 text-orange-500" />
                        {user?.username} - Cihaz Listesi
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-white transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-auto p-4">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center h-full text-gray-400">
                            <Loader2 className="w-8 h-8 animate-spin mb-2" />
                            <p>Cihazlar yükleniyor...</p>
                        </div>
                    ) : devices.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full text-gray-400 bg-[#0a0e13]/50 rounded-lg border border-white/5 m-4">
                            <Smartphone className="w-12 h-12 text-gray-600 mb-2" />
                            <p>Bu kullanıcının kayıtlı cihazı bulunmamaktadır.</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto rounded-lg border border-white/10">
                            <table className="w-full text-left border-collapse">
                                <thead className="bg-[#252525] text-gray-400 text-xs uppercase font-medium">
                                    <tr>
                                        <th className="p-3 border-b border-white/10">Platform</th>
                                        <th className="p-3 border-b border-white/10">Cihaz Adı</th>
                                        <th className="p-3 border-b border-white/10">Device ID</th>
                                        <th className="p-3 border-b border-white/10">IP Adresi</th>
                                        <th className="p-3 border-b border-white/10">Tokens (FCM/Apns/Web)</th>
                                        <th className="p-3 border-b border-white/10">Durum</th>
                                        <th className="p-3 border-b border-white/10">Son Kullanım</th>
                                        <th className="p-3 border-b border-white/10">Oluşturulma</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-white/5">
                                    {devices.map((device, index) => (
                                        <tr key={index} className="hover:bg-white/5 transition-colors text-sm text-gray-300">
                                            <td className="p-3">
                                                <div className="flex items-center gap-2">
                                                    {getPlatformIcon(device.platform)}
                                                    <span>{device.platform || 'Unknown'}</span>
                                                </div>
                                            </td>
                                            <td className="p-3 font-medium text-white">{device.device_name || 'N/A'}</td>
                                            <td className="p-3 font-mono text-xs text-gray-400">{device.device_id || 'N/A'}</td>
                                            <td className="p-3 font-mono text-xs">{device.ip_address || 'N/A'}</td>
                                            <td className="p-3 space-y-1">
                                                {/* FCM Token */}
                                                {device.fcm_token && (
                                                    <div className="flex items-center gap-1 group relative cursor-pointer" onClick={() => copyToClipboard(device.fcm_token)}>
                                                        <span className="text-[10px] bg-orange-500/20 text-orange-400 px-1 rounded">FCM</span>
                                                        <span className="text-xs truncate max-w-[100px] font-mono group-hover:text-white transition-colors">{device.fcm_token.substring(0, 10)}...</span>
                                                        <Copy className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity ml-auto" />
                                                    </div>
                                                )}
                                                {/* APNS Token */}
                                                {device.apns_token && (
                                                    <div className="flex items-center gap-1 group relative cursor-pointer" onClick={() => copyToClipboard(device.apns_token)}>
                                                        <span className="text-[10px] bg-blue-500/20 text-blue-400 px-1 rounded">APNS</span>
                                                        <span className="text-xs truncate max-w-[100px] font-mono group-hover:text-white transition-colors">{device.apns_token.substring(0, 10)}...</span>
                                                        <Copy className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity ml-auto" />
                                                    </div>
                                                )}
                                                {/* Web Push */}
                                                {device.web_push_token && (
                                                    <div className="flex items-center gap-1 group relative cursor-pointer" onClick={() => copyToClipboard(device.web_push_token)}>
                                                        <span className="text-[10px] bg-purple-500/20 text-purple-400 px-1 rounded">WEB</span>
                                                        <span className="text-xs truncate max-w-[100px] font-mono group-hover:text-white transition-colors">{device.web_push_token.substring(0, 10)}...</span>
                                                        <Copy className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity ml-auto" />
                                                    </div>
                                                )}
                                                {!device.fcm_token && !device.apns_token && !device.web_push_token && <span className="text-gray-500 text-xs">Token yok</span>}
                                            </td>
                                            <td className="p-3">
                                                <span className={`px-2 py-0.5 rounded text-xs font-medium ${device.push_enabled ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                                                    {device.push_enabled ? 'Aktif' : 'Pasif'}
                                                </span>
                                            </td>
                                            <td className="p-3 text-xs text-gray-400">{formatDate(device.last_used_at)}</td>
                                            <td className="p-3 text-xs text-gray-400">{formatDate(device.created_at)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-white/10 bg-[#252525] flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors text-sm font-medium"
                    >
                        Kapat
                    </button>
                </div>
            </div>
        </div>
    );
};

export default UserDevicesModal;
